/**
 * Widget Revenus Propriétaire
 * Phase 3.2 - Analyse des revenus optimisée
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

import { DashboardWidget, StatusIndicator } from '../DashboardBase';
import { 
  formatCurrency, 
  STATUS_COLORS, 
  PERIOD_LABELS 
} from '../../../constants/dashboardConstants';

const OwnerRevenueWidget = ({ data, loading, period, onRefresh }) => {
  if (!data && !loading) {
    return (
      <DashboardWidget
        title="Revenus"
        loading={loading}
        onRefresh={onRefresh}
      >
        <Typography color="text.secondary">
          Aucune donnée de revenus disponible
        </Typography>
      </DashboardWidget>
    );
  }

  const {
    total = 0,
    pending = 0,
    completed = 0,
    failed = 0,
    by_method = {},
    by_status = {}
  } = data || {};

  // Calcul des pourcentages
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const pendingRate = total > 0 ? Math.round((pending / total) * 100) : 0;

  // Méthodes de paiement
  const paymentMethods = Object.entries(by_method).map(([method, amount]) => ({
    method,
    amount,
    percentage: total > 0 ? Math.round((amount / total) * 100) : 0
  }));

  // Statuts de paiement pour le tableau
  const paymentStatuses = Object.entries(by_status).map(([status, amount]) => ({
    status,
    amount,
    percentage: total > 0 ? Math.round((amount / total) * 100) : 0
  }));

  const getMethodLabel = (method) => {
    const labels = {
      cash: 'Espèces',
      bank_transfer: 'Virement',
      card: 'Carte',
      stripe_complete: 'Stripe'
    };
    return labels[method] || method;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      completed: 'Complété',
      failed: 'Échoué',
      partial: 'Partiel',
      refunded: 'Remboursé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  return (
    <DashboardWidget
      title={`Revenus - ${PERIOD_LABELS[period]}`}
      loading={loading}
      onRefresh={onRefresh}
      height={500}
    >
      {/* Résumé des revenus */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <MoneyIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
            <Typography variant="h5" color="success.main" fontWeight="bold">
              {formatCurrency(completed)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenus confirmés
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
            <ScheduleIcon sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
            <Typography variant="h5" color="warning.main" fontWeight="bold">
              {formatCurrency(pending)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En attente
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
            <Typography variant="h5" color="primary.main" fontWeight="bold">
              {completionRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Taux de réalisation
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tableau des statuts de paiement */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Répartition par statut
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Montant</TableCell>
                <TableCell align="right">Pourcentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentStatuses.map((item) => (
                <TableRow key={item.status}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StatusIndicator status={`payment_${item.status}`} />
                      <Typography variant="body2">
                        {getStatusLabel(item.status)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(item.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${item.percentage}%`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Méthodes de paiement */}
      {paymentMethods.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Répartition par méthode de paiement
          </Typography>
          
          <Grid container spacing={1}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} sm={6} key={method.method}>
                <Box display="flex" justifyContent="space-between" alignItems="center" p={1}>
                  <Typography variant="body2">
                    {getMethodLabel(method.method)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(method.amount)}
                    </Typography>
                    <Chip
                      label={`${method.percentage}%`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </DashboardWidget>
  );
};

export default OwnerRevenueWidget;
